import { DateTime, Duration } from 'luxon'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { publisher } from '@/constants/seo'
import { slugs } from '@/constants/slugs'
import { ProfileContextProvider } from '@/contexts/ProfileContext'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { Seo } from '@/layout/Seo'
import { VideoObjectJsonLd } from '@/layout/Seo/JsonLd'
import { getMediaMetaData } from '@/layout/Seo/SeoHelper'
import { GetAppBannerProvider } from '@/organisms/GetAppBanner'
import { getWebClient } from '@/services/ApolloClient'
import { getLivestreamCta, LivestreamCta } from '@/services/CtaService'
import { createLivestreamDeepLink } from '@/services/DeepLinkService'
import { getLivestreamMetaData, LivestreamMeta } from '@/services/LivestreamService'
import { ProjectMeta } from '@/services/ProjectsService'
import { getLocaleFromParams } from '@/utils/LocaleUtil'
import { getStaticPropsErrorHandler, getUrlFromThisFilePath } from '@/utils/nextUtils/nextErrorHandlers'
import { shouldHideFromSearchEngines } from '@/utils/seo/searchEngineHelper'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { LivestreamView } from '@/views/LivestreamView'

interface LivestreamPageProps {
  deepLink: string
  locale: string
  livestreamMeta: LivestreamMeta
  livestreamCta: LivestreamCta
  projectMeta: ProjectMeta
  streamId: string
}

export const LivestreamPage: NextPage<LivestreamPageProps> = (props) => {
  const { t } = useTranslate('livestream')
  const { livestreamMeta, projectMeta, livestreamCta } = props
  const basePath = getBasePath(props.locale)
  const url = `${basePath}${paths.livestream.index}/${props.streamId}`
  const meta = getMediaMetaData({
    cloudinaryImagePath: livestreamMeta.bannerCloudinaryPath,
    description: livestreamMeta.description,
    title: t('livestreamTitle', `{{projectName}} Livestream: {{livestreamName}}`, {
      projectName: props.projectMeta.name,
      livestreamName: livestreamMeta.name,
    }),
    url,
  })
  const startDate = DateTime.fromISO(livestreamMeta.startAt)
  const endDate = livestreamMeta.endedAt ? DateTime.fromISO(livestreamMeta.endedAt) : startDate.plus({ hours: 1 }) // Add a default of 1 hour if endedAt is not specified
  const duration = livestreamMeta?.durationSeconds
    ? Duration.fromDurationLike({ hour: 0, minute: 0, second: livestreamMeta.durationSeconds }).normalize().toISO()
    : endDate.diff(startDate).shiftTo('hours', 'minutes', 'seconds').toISO()
  const shouldHide = shouldHideFromSearchEngines(props.projectMeta)

  return (
    <>
      <Seo {...meta} nofollow={shouldHide} noindex={shouldHide} path={`${paths.livestream.index}/${props.streamId}`} />
      <VideoObjectJsonLd
        url={url}
        name={livestreamMeta.name}
        description={livestreamMeta.description}
        duration={duration}
        thumbnailUrl={`https://images.angelstudios.com/image/upload/b_rgb:000000,c_fill,h_630,w_1200,g_north/${livestreamMeta?.bannerCloudinaryPath}`}
        contentUrl={livestreamMeta.url}
        uploadDate={startDate.toISO()}
        expires={endDate.toISO()}
        publication={{
          type: 'BroadcastEvent',
          isLiveBroadcast: true,
          startDate: startDate.toISO(),
          endDate: endDate.toISO(),
        }}
        publisher={publisher}
      />
      <ProfileContextProvider>
        <ProjectMetaProvider project={projectMeta}>
          <GetAppBannerProvider deepLink={props.deepLink} projectSlug={projectMeta.slug} context="livestream">
            <LivestreamView
              livestreamCta={livestreamCta}
              livestreamMeta={livestreamMeta}
              projectMeta={projectMeta}
              streamId={props.streamId}
            />
          </GetAppBannerProvider>
        </ProjectMetaProvider>
      </ProfileContextProvider>
    </>
  )
}

const getParamsFromPath = (id: string) => {
  if (isNaN(id as unknown as number)) {
    return {
      guid: id,
    }
  } else {
    return {
      id,
    }
  }
}

export const getStaticProps: GetStaticProps = async ({ params, preview = false }) =>
  getStaticPropsErrorHandler({ requestUrl: getUrlFromThisFilePath(params) }, async () => {
    try {
      const locale = getLocaleFromParams(params)
      const client = getWebClient({ locale })
      const { id, guid } = getParamsFromPath(params?.id as string)

      const { livestreamMeta, projectMeta } = await getLivestreamMetaData({ id, guid }, client, { locale, preview })

      if (livestreamMeta.projectSlug === slugs.theChosen) {
        return {
          redirect: {
            destination: paths.watch.index,
            permanent: false,
          },
        }
      }

      // Return 404 if the livestream becomes archived
      if (livestreamMeta.isArchived) {
        return {
          notFound: true,
          revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
        }
      }
      const shouldRedirectToGuid =
        typeof id !== 'undefined' && !isNaN(id as unknown as number) && parseInt(id as string) < 550

      if (shouldRedirectToGuid) {
        return {
          redirect: {
            destination: `/livestreams/${livestreamMeta?.guid}`,
            permanent: true,
          },
        }
      }

      const livestreamCta = await getLivestreamCta(livestreamMeta.projectSlug, livestreamMeta.guid, { locale, preview })
      const path = `${paths.livestream.index}/${livestreamMeta?.guid}`

      const deepLink = await createLivestreamDeepLink(
        {
          path,
          guid: livestreamMeta?.guid,
          projectSlug: projectMeta?.slug,
          projectName: projectMeta?.name,
          contentName: livestreamMeta?.name as string,
          audioLocale: locale,
          captionLocale: locale,
        },
        {
          campaign: 'force-app-install',
          feature: 'app-prompt',
          channel: 'angel-web',
        },
      )

      return {
        props: {
          deepLink,
          locale,
          livestreamMeta,
          livestreamCta,
          projectMeta,
          streamId: params?.id,
          ...(await loadTranslations(locale, [
            'common',
            'ads',
            'livestream',
            'chatbot',
            'account',
            'guild-benefits',
            'guild',
          ])),
        },
        revalidate: livestreamMeta.endedAt ? false : Durations.FIVE_MINUTES_IN_SECONDS,
      }
    } catch (e) {
      return {
        notFound: true,
      }
    }
  })

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export default LivestreamPage
